/**
 * useFeatureShortcuts — enregistre les raccourcis clavier des features actives.
 *
 * @remarks
 * Lit `manifest.shortcuts` des features actives sur le contexte courant
 * (filtrées par `enabledWhen`), gère le scoping `'global' | 'context'`,
 * et bind les handlers via un keydown listener global.
 *
 * Évite les conflits entre features : chaque feature déclare ses raccourcis,
 * le shell les agrège et active uniquement ceux du contexte courant.
 *
 * Exécute aussi la commande `shell:navigate` : un handler de manifest vit hors
 * de React et ne peut pas appeler `useNavigate` lui-même.
 *
 * @example Dans le shell :
 * ```ts
 * useFeatureShortcuts(currentContext, appCtx);
 * ```
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCommand } from '@/app/commands/command-bus';
import { featureRegistry, type Context, type AppContext } from '@/app/registry';
import type { ShortcutBinding } from '@/app/registry';
import { isMacPlatform } from '@/lib/utils/format-shortcut';

/** Champs où une frappe est une saisie, pas une commande. */
const EDITABLE_SELECTOR =
  'input, textarea, select, [contenteditable]:not([contenteditable="false"])';

function isEditableTarget(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(EDITABLE_SELECTOR) !== null;
}

/**
 * Match `e.key`/`e.code` contre une string keys au format mousetrap-like
 * (`'mod+k'`, `'mod+shift+p'`, `'mod+enter'`). Simple, suffit pour démarrer.
 *
 * @internal Pour des cas plus complexes, intégrer `react-hotkeys-hook`.
 */
function matchesKeyBinding(e: KeyboardEvent, keys: string): boolean {
  const parts = keys
    .toLowerCase()
    .split('+')
    .map((p) => p.trim());
  const expectedKey = parts[parts.length - 1];
  const expectedMods = new Set(parts.slice(0, -1));

  // mod = Cmd sur Mac, Ctrl ailleurs
  const modActive = isMacPlatform() ? e.metaKey : e.ctrlKey;
  const wantsMod = expectedMods.has('mod') || expectedMods.has('cmd') || expectedMods.has('ctrl');

  if (wantsMod !== modActive) return false;
  if (expectedMods.has('shift') !== e.shiftKey) return false;
  if (expectedMods.has('alt') !== e.altKey) return false;

  if (expectedKey === undefined) return false;

  const eKey = e.key.toLowerCase();
  // Avec Alt, macOS renvoie le caractère de la touche Option (Option+B → « ∫ ») :
  // pour une lettre, on accepte aussi la touche physique. Seulement avec Alt,
  // car `e.code` suit la position QWERTY (A et Q inversés sur AZERTY).
  if (e.altKey && /^[a-z]$/.test(expectedKey)) {
    return eKey === expectedKey || e.code === `Key${expectedKey.toUpperCase()}`;
  }
  // Mapping minimal pour les keys spéciales courantes
  if (expectedKey === 'enter') return eKey === 'enter';
  if (expectedKey === 'esc' || expectedKey === 'escape') return eKey === 'escape';
  if (expectedKey === '→' || expectedKey === 'right') return eKey === 'arrowright';
  if (expectedKey === '←' || expectedKey === 'left') return eKey === 'arrowleft';
  return eKey === expectedKey;
}

export function useFeatureShortcuts(context: Context, appCtx: AppContext): void {
  // `getActiveShortcuts` retourne un nouveau tableau à chaque appel (filter/flatMap),
  // donc sans memo le useEffect re-bind à chaque render du shell.
  const shortcuts: readonly ShortcutBinding[] = useMemo(
    () => featureRegistry.getActiveShortcuts(context, appCtx),
    [context, appCtx],
  );

  const navigate = useNavigate();
  const handleNavigate = useCallback(
    (to: string) => {
      void navigate(to);
    },
    [navigate],
  );
  useCommand('shell:navigate', handleNavigate);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      for (const s of shortcuts) {
        if (matchesKeyBinding(e, s.keys)) {
          // La touche reste au champ : ni handler, ni preventDefault.
          if (s.ignoreInEditable === true && isEditableTarget(e.target)) return;
          if (s.preventDefault !== false) e.preventDefault();
          s.handler(e);
          return; // un seul match suffit
        }
      }
    };

    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [shortcuts]);
}
